 
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getJobs, getUsers, getProjects, createJob, updateJob, deleteJob } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Briefcase,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  User,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

const AdminJobsManagement = () => {
  const { t, locale } = useLanguage();
  const statusLabels = {
    planned: t('portalUi.status.planned', 'Plānots'),
    in_progress: t('portalUi.status.inProgress', 'Procesā'),
    completed: t('portalUi.status.completed', 'Pabeigts')
  };

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-green-100 text-green-700 border-green-200'
  };

  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    project_id: '',
    work_type: 'cits',
    status: 'planned',
    start_date: '',
    planned_end_date: '',
    responsible_person: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [jobsData, usersData, projectsData] = await Promise.all([
        getJobs(),
        getUsers(),
        getProjects()
      ]);
      setJobs(jobsData);
      setUsers(usersData.filter(u => u.role === 'client'));
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title,
        description: job.description || '',
        client_id: job.client_id,
        project_id: job.project_id,
        work_type: job.work_type || 'cits',
        status: job.status,
        start_date: job.start_date ? job.start_date.split('T')[0] : '',
        planned_end_date: job.planned_end_date ? job.planned_end_date.split('T')[0] : '',
        responsible_person: job.responsible_person || '',
        notes: job.notes || ''
      });
    } else {
      setEditingJob(null);
      setFormData({
        title: '',
        description: '',
        client_id: '',
        project_id: '',
        work_type: 'cits',
        status: 'planned',
        start_date: '',
        planned_end_date: '',
        responsible_person: '',
        notes: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        planned_end_date: formData.planned_end_date ? new Date(formData.planned_end_date).toISOString() : null,
      };

      if (editingJob) {
        await updateJob(editingJob.id, submitData);
        toast.success(t('portalUi.admin.jobs.toastUpdated', 'Darbs atjaunināts'));
      } else {
        await createJob(submitData);
        toast.success(t('portalUi.admin.jobs.toastCreated', 'Darbs izveidots'));
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.jobs.toastError', 'Kļūda'));
    }
  };

  const handleDelete = async (jobId, title) => {
    if (window.confirm(t('portalUi.admin.jobs.confirmDelete', 'Vai tiešām vēlaties dzēst darbu "{title}"?').replace('{title}', title))) {
      try {
        await deleteJob(jobId);
        toast.success(t('portalUi.admin.jobs.toastDeleted', 'Darbs dzēsts'));
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('portalUi.admin.jobs.toastDeleteError', 'Kļūda'));
      }
    }
  };

  const getClientName = (clientId) => {
    const user = users.find(u => u.id === clientId);
    return user?.client_name || t('portalUi.admin.jobs.unknownClient', 'Nezināms');
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = job.title.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [jobs, search, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="admin-jobs-title">{t('portalUi.admin.jobs.title', 'Darbi')}</h1>
          <p className="text-[#2E2E2E]/60 mt-1" data-testid="admin-jobs-subtitle">{t('portalUi.admin.jobs.subtitle', 'Pārvaldiet darbus un to statusus')}</p>
        </div>
        <Button className="bg-[#1F3D2B] hover:bg-[#2a5039]" onClick={() => handleOpenModal()} data-testid="admin-jobs-add">
          <Plus className="w-4 h-4 mr-2" />
          {t('portalUi.admin.jobs.addButton', 'Pievienot darbu')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
          <Input
            placeholder={t('portalUi.admin.jobs.searchPlaceholder', 'Meklēt darbus...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="admin-jobs-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'planned', 'in_progress', 'completed'].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className={statusFilter === status ? 'bg-[#1F3D2B]' : ''}
              data-testid={`admin-jobs-filter-${status}`}
            >
              {status === 'all' ? t('portalUi.admin.jobs.filterAll', 'Visi') : statusLabels[status]}
            </Button>
          ))}
        </div>
      </div>

      {/* Jobs Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8faf8]">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.job', 'Darbs')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.client', 'Klients')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.status', 'Statuss')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.dates', 'Datumi')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.responsible', 'Atbildīgais')}</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.jobs.table.actions', 'Darbības')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-[#f8faf8] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          job.status === 'completed' ? 'bg-green-100' :
                          job.status === 'in_progress' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          {job.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : job.status === 'in_progress' ? (
                            <Clock className="w-5 h-5 text-amber-600" />
                          ) : (
                            <Briefcase className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#2E2E2E]">{job.title}</p>
                          {job.description && (
                            <p className="text-sm text-[#2E2E2E]/60 truncate max-w-xs">{job.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#2E2E2E]/40" />
                        <span className="text-[#2E2E2E]/70">{getClientName(job.client_id)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[job.status]}`}>
                        {statusLabels[job.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {job.planned_end_date && (
                          <div className="flex items-center gap-1 text-[#2E2E2E]/60">
                            <Calendar className="w-4 h-4" />
                            {t('portalUi.admin.jobs.deadlineLabel', 'Līdz')}: {new Date(job.planned_end_date).toLocaleDateString(locale)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#2E2E2E]/70">
                      {job.responsible_person || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(job)}
                          className="text-[#1F3D2B] hover:bg-[#1F3D2B]/10"
                          data-testid={`admin-jobs-edit-${job.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(job.id, job.title)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`admin-jobs-delete-${job.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="admin-jobs-modal-title">
              {editingJob ? t('portalUi.admin.jobs.modal.editTitle', 'Rediģēt darbu') : t('portalUi.admin.jobs.modal.addTitle', 'Pievienot darbu')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-jobs-form">
            <div className="space-y-2">
              <Label>{t('portalUi.admin.jobs.modal.titleLabel', 'Darba nosaukums')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('portalUi.admin.jobs.modal.titlePlaceholder', 'Augošu koku dastošana')}
                required
                data-testid="admin-jobs-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.jobs.modal.descriptionLabel', 'Apraksts')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('portalUi.admin.jobs.modal.descriptionPlaceholder', 'Darba apraksts...')}
                rows={3}
                data-testid="admin-jobs-description"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.jobs.modal.clientLabel', 'Klients')} *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(value) => setFormData({ ...formData, client_id: value, project_id: '' })}
              >
                <SelectTrigger data-testid="admin-jobs-client">
                  <SelectValue placeholder={t('portalUi.admin.jobs.modal.clientPlaceholder', 'Izvēlieties klientu')} />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.jobs.modal.projectLabel', 'Projekts')} *</Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                disabled={!formData.client_id}
              >
                <SelectTrigger data-testid="admin-jobs-project">
                  <SelectValue placeholder={formData.client_id ? t('portalUi.admin.jobs.modal.projectPlaceholder', 'Izvēlieties projektu') : t('portalUi.admin.jobs.modal.projectPlaceholderEmpty', 'Vispirms izvēlieties klientu')} />
                </SelectTrigger>
                <SelectContent>
                  {projects
                    .filter(p => p.client_id === formData.client_id)
                    .map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('portalUi.admin.jobs.modal.statusLabel', 'Statuss')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="admin-jobs-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">{t('portalUi.status.planned', 'Plānots')}</SelectItem>
                    <SelectItem value="in_progress">{t('portalUi.status.inProgress', 'Procesā')}</SelectItem>
                    <SelectItem value="completed">{t('portalUi.status.completed', 'Pabeigts')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('portalUi.admin.jobs.modal.responsibleLabel', 'Atbildīgais')}</Label>
                <Input
                  value={formData.responsible_person}
                  onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                  placeholder={t('portalUi.admin.jobs.modal.responsiblePlaceholder', 'Jānis Bērziņš')}
                  data-testid="admin-jobs-responsible"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('portalUi.admin.jobs.modal.startDateLabel', 'Sākuma datums')}</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="admin-jobs-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('portalUi.admin.jobs.modal.endDateLabel', 'Plānotais beigu datums')}</Label>
                <Input
                  type="date"
                  value={formData.planned_end_date}
                  onChange={(e) => setFormData({ ...formData, planned_end_date: e.target.value })}
                  data-testid="admin-jobs-end-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.jobs.modal.notesLabel', 'Piezīmes klientam')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('portalUi.admin.jobs.modal.notesPlaceholder', 'Piezīmes, kas būs redzamas klientam...')}
                rows={2}
                data-testid="admin-jobs-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="admin-jobs-cancel">
                {t('portalUi.admin.jobs.modal.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-jobs-submit">
                {editingJob ? t('portalUi.admin.jobs.modal.save', 'Saglabāt') : t('portalUi.admin.jobs.modal.create', 'Izveidot')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobsManagement;
